import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
} from "@/components/ui/dialog";
import BorderedIconWrapper from "../common/BorderIconWrapper";
import { Typography } from "../ui/typography";
import { Chip } from "../common/Chip";
import CrossIcon from "@/assets/svg/crossIcon.svg";
import CrossDarkIcon from "@/assets/svg/crossDarkIcon.svg";

type DialogBoxProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon?: string | React.ReactNode;
  iconAlt: string;
  title: string;
  chip?: {
    label: string;
    value: string;
    variant: "success" | "warning" | "danger";
  };
  searchComponent?: React.ReactNode;
  filterComponent?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

const DialogBox = ({
  open,
  onOpenChange,
  icon,
  iconAlt,
  title,
  chip,
  children,
  className,
}: DialogBoxProps) => {
  return (
    <Dialog 
      open={open} 
      onOpenChange={(newOpen) => {
        if (!newOpen) return;
        onOpenChange(newOpen);
      }}
    >
      <DialogContent
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
        className={`gap-0 p-0 w-[96%] md:w-[98%] lg:w-full max-w-5xl max-h-[80%] overflow-auto bg-grey-100 dark:bg-blue-900 !animate-none data-[state=open]:fade-in data-[state=open]:zoom-in-95 rounded-[6px] ${className || ""}`}>
        <DialogHeader className='h-12 py-[9px] px-[18px] border-b border-grey-300 dark:border-grey-600 rounded-tl-[6px] rounded-tr-[6px] rounded-bl-[1px] rounded-br-[1px]'>
          <div className='flex items-center justify-between gap-4 h-full'>
            {/* Left side - Icon and Title */}
            <div className='flex items-center gap-2'>
              <BorderedIconWrapper>
                {typeof icon === "string" ? (
                  <img src={icon} alt={iconAlt} className='w-4 h-4' />
                ) : (
                  icon
                )}{" "}
              </BorderedIconWrapper>
              <Typography variant='semibold-16'>{title}</Typography>
            </div>

            {/* Right side - Chip and Close button */}
            <div className='flex items-center gap-3'>
              {chip && (
                <Chip
                  label={chip.label}
                  value={chip.value}
                  variant={chip.variant}
                />
              )}
              <DialogClose asChild>
                <div
                  role='button'
                  aria-label='Close'
                  onClick={() => onOpenChange(false)}
                  className='
                  h-[30px] w-[30px]
                  bg-grey-200
                  hover:bg-grey-300
                  dark:bg-[#32414A]
                  dark:hover:bg-gray-600
                  text-muted-foreground
                  hover:none
                  flex items-center justify-center
                  rounded-md
                  cursor-pointer
                  transition-all
                   '>
                  <img
                    src={CrossIcon}
                    alt='crossicon'
                    className=' block dark:hidden'
                  />
                  <img
                    src={CrossDarkIcon}
                    alt='crossicon'
                    className='hidden dark:block'
                  />
                </div>
              </DialogClose>
            </div>
          </div>
        </DialogHeader>

        {/* Content Area */}
        <div className='px-3 py-2.5 overflow-auto flex-1'>{children}</div>
      </DialogContent>
    </Dialog>
  );
};

export type { DialogBoxProps };
export { DialogBox };
